/**
 * Default store copy for a school app, filled from the school's own values.
 *
 * The strings are the fleet convention the browser runner files today
 * (`apps/browser-runner/templates/asc-listing.json`, `asc-app-info.json`, `play-store-settings.json`,
 * `play-app-access.json`); change them there and here together. The Play short description has no
 * runner template and is new copy.
 *
 * A default is a SUGGESTION: the dashboard pre-fills never-set fields with it and a person saves.
 * URLs are only suggested once the caller has checked they resolve (see the defaults route).
 */

import type { StoreListingRecord, StoreListingStore } from './mobile-app-store-listing'

export interface StoreListingDefaultVars {
  /** `MobileApp.name`. */
  appName: string
  /** `MobileApp.displayName ?? name` — the store name. */
  displayName: string
}

/** The description's closing sign-in line — the one place Blackbaud is named. */
export const storeListingSignInLine = (displayName: string) =>
  `Sign in with the same account you use for the ${displayName} Blackbaud portal.`

const DESCRIPTION = (v: StoreListingDefaultVars) =>
  `Stay connected with ${v.displayName}.\n\n` +
  `The ${v.appName} app brings ${v.displayName}'s news, events, classes, and community together in one place for families, students, and faculty.\n\n` +
  '• School news and announcements\n' +
  '• Calendars and upcoming events\n' +
  '• Classes and schedules\n' +
  '• Directory of students, families, and faculty\n' +
  '• Groups for teams, clubs, and classes\n' +
  '• Notifications so you never miss an update\n\n' +
  `${storeListingSignInLine(v.displayName)}\n`

const WHATS_NEW =
  '• Refreshed UI elements for a smoother experience\n• Performance improvements across the app\n• Security enhancements and stability updates\n• Various bug fixes and behind-the-scenes improvements'

const SHORT_DESCRIPTION_MAX = 80
const shortDescription = (v: StoreListingDefaultVars) => {
  const named = `${v.displayName} news, events, and school community in one app.`
  return named.length <= SHORT_DESCRIPTION_MAX ? named : 'School news, events, and community in one app.'
}

/**
 * The recording App Review asked for (St. Mark's 1.9.0, Guideline 2.5.4, 2026-10-05): an incoming
 * call ringing through CallKit, answered, and carrying on in the background, filmed on a physical
 * iPhone with a second device because an iPhone cannot screen-record while a CallKit call rings.
 * Shot in the demo app, which runs the same calling code as every school app. Public on Drive,
 * owned by apps@schooljourney.com; keep it shared "anyone with the link" or every filed note breaks.
 */
export const CALLING_REVIEW_RECORDING_URL =
  'https://drive.google.com/file/d/1jihl-xDHI04zh9URuRgIOBeqqlVhYLmq/view?usp=sharing'

/**
 * Why every build declares `voip` / `audio` (SCH-2496: every app rings), and how a reviewer sees the
 * feature. Apple rejects those declarations without a visible calling feature (Guideline 2.5.4), and
 * a reviewer cannot make a call ring on their own: only verified school members, signed in through
 * the school's Blackbaud portal, can call each other. So the paragraph leads with the recording and
 * says why it comes from the demo app. It offers no live call and no steps to place one: the demo
 * account cannot place or receive calls, so either would promise the reviewer something that fails.
 */
const REVIEW_NOTES_CALLING =
  'VOICE AND VIDEO CALLING (why "voip" and "audio" are declared)\n' +
  '- Members can call each other. "voip" lets an incoming call ring through CallKit when the app is in the background or closed; "audio" keeps the call going in the background.\n' +
  '- Calls do not work on the demo account. Only verified school members (signed in through the school’s Blackbaud portal) can call.\n' +
  `- Recording of a call ringing, answered and continuing in the background, filmed on a physical iPhone with our demo app (same calling code): ${CALLING_REVIEW_RECORDING_URL}\n\n`

/**
 * Apple asked under Guideline 2.1 whether the app uses Widgets (Thomas Jefferson IDS 1.9.1,
 * 2026-10-08). It has no WidgetKit target; its only app extensions are OneSignal's Notification
 * Service Extension and expo-sharing's Share Extension (apps/mobile/app.config.ts). Keep this in step
 * with the extensions a build actually embeds.
 */
const REVIEW_NOTES_EXTENSIONS =
  'WIDGETS AND APP EXTENSIONS\n' +
  '- No Widgets: nothing on the Home Screen, Lock Screen, Today View or StandBy.\n' +
  '- Two extensions: Notification Service (images in push notifications, correct badge count) and Share (share photos, videos or files into a message or post).\n' +
  '- The App Group only passes data between these extensions and the app.\n'

/** Where real sign-in stands: Blackbaud sign-in already works; approval only removes Demo login. */
const SIGN_IN_FACT =
  'School members sign in with their school’s Blackbaud account, which works today. The Demo login button is removed after approval.'

const REVIEW_NOTES_SIGN_IN = `- ${SIGN_IN_FACT}`

/**
 * The calling paragraph filed from SCH-2808 until the notes were shortened (2026-10-08). Kept
 * verbatim so saved listings are upgraded.
 */
export const RETIRED_REVIEW_NOTES_CALLING_LONG =
  'VOICE AND VIDEO CALLING\n\n' +
  'This app includes voice and video calls between members of the school community. That feature is why the app declares the "voip" and "audio" background modes: "voip" lets PushKit wake the app so an incoming call rings the device through CallKit while the app is in the background or closed, and "audio" keeps call audio running while the app is in the background.\n\n' +
  `Recording on a physical iPhone: ${CALLING_REVIEW_RECORDING_URL}\n` +
  'It shows an incoming call ringing, being answered, and continuing in the background. An iPhone cannot screen-record while receiving a CallKit call, so it is filmed with a second device. In the real school, calls can only be placed between verified members, who sign in through the school’s Blackbaud portal, which admits only that school’s own students, parents and staff. We cannot create a second account in the real school to call you, so the recording uses our demo app, which runs the same calling code as this app.\n\n' +
  'Calling is not available on the demo account: Demo login signs you in to a test school with fake data, and calls cannot be placed to or from it. The recording above is how the feature can be seen.\n\n'

/**
 * The closing line every saved note ended with until 2026-10-08. It was wrong: real Blackbaud
 * sign-in already works; approval only removes the Demo login button. Swapped for the sign-in line
 * plus the extensions section, which saved listings also lack.
 */
export const RETIRED_REVIEW_NOTES_AFTER_APPROVAL =
  'After approval, real-school login will be enabled by feature flag and the app will be released manually.\n'

const APP_ACCESS_CALLING = `\n\nVoice and video calls: the app includes calls between members of the school community. Calling is not available on the demo account, so a recording of a call ringing, being answered and continuing in the background is here: ${CALLING_REVIEW_RECORDING_URL}`

/**
 * The Play access paragraph from SCH-2496 until SCH-2808. It told the reviewer to place a call from
 * the demo account and offered to call it, and the demo account cannot place or receive calls.
 * Kept verbatim so saved listings are upgraded.
 */
export const RETIRED_APP_ACCESS_RINGING_LIVE_CALL =
  '\n\nVoice and video calls: after Demo login, open the chat tab, open any conversation and tap the phone or video icon to place a call. An incoming call rings only when another person calls; contact us through the developer contact and we will call the demo account so you can see the incoming call screen.'

/**
 * The in-app calling paragraphs apps were given before every build rang (SCH-2496), kept verbatim
 * only so saved listings can be recognised and upgraded. They tell a reviewer the app "declares no
 * VoIP or audio background mode", which every build now does: submitted as is, that is the
 * Guideline 2.5.4 rejection.
 */
export const RETIRED_REVIEW_NOTES_IN_APP_CALLING =
  'VOICE AND VIDEO CALLING\n\n' +
  'This app includes voice and video calls between members of the school community, available while the app is open. The app declares no VoIP or audio background mode: an incoming call appears on the incoming call screen inside the app while it is open.\n\n' +
  'To place a call: after Demo login, open the chat tab, open any conversation, and tap the phone or video icon in the conversation header. The outgoing call screen appears and the other member sees the incoming call in their app.\n\n' +
  'To receive a call: a call arrives only when another person places it, and the demo school has no automated caller. Reply to this submission in App Store Connect or contact us using the details above, and we will call the demo account from a staff account in the same test school while you have the app open, so you can see the incoming call screen and answer it.\n\n'

/**
 * The ringing paragraph filed from SCH-2496 until the recording was added: it offered only to call
 * the reviewer, and App Review rejected St. Mark's 1.9.0 under Guideline 2.5.4 for "voip" and
 * "audio" because it could not find the feature. Kept verbatim so saved listings are upgraded.
 */
export const RETIRED_REVIEW_NOTES_RINGING_WITHOUT_RECORDING =
  'VOICE AND VIDEO CALLING\n\n' +
  'This app includes voice and video calls between members of the school community. That feature is why the app declares the "voip" and "audio" background modes: an incoming call rings the device through CallKit while the app is in the background or closed, and call audio continues while the app is in the background.\n\n' +
  'To place a call: after Demo login, open the chat tab, open any conversation, and tap the phone or video icon in the conversation header. The outgoing call screen appears and the other member’s device rings.\n\n' +
  'To receive a call: a call rings only when another person places it, and the demo school has no automated caller. Reply to this submission in App Store Connect or contact us using the details above, and we will call the demo account from a staff account in the same test school while you have the app open, in the background, or closed, so you can see the incoming call screen and answer it.\n\n'

export const RETIRED_APP_ACCESS_IN_APP_CALLING =
  '\n\nVoice and video calls work while the app is open: after Demo login, open the chat tab, open any conversation and tap the phone or video icon to place a call. An incoming call appears only while the app is open; contact us through the developer contact and we will call the demo account so you can see the incoming call screen.'

/**
 * Where Demo login lands. Since 2026-10-22 the shared demo user is test student Tyler Herbert at
 * Waypoint School, the demo-platform school: the demo-token mint refuses a demo user who holds a
 * membership at any non-demo school (SCH-3422), so it no longer signs in to SKY Developer Cohort as
 * Joe Newberry (SCH-4148).
 */
const DEMO_SCHOOL = 'Waypoint School, a TEST school with fake data (test student Tyler Herbert)'

/** The demo-school phrase filed until SCH-4148. Kept verbatim so saved listings are upgraded. */
export const RETIRED_DEMO_SCHOOL = 'a TEST school with fake data (test student Joe Newberry)'

/**
 * Swap a saved listing's retired calling paragraph (the in-app one from before SCH-2496, or the
 * ringing one from before the recording) and its retired demo-school phrase for the current ones,
 * leaving everything else in the text as it is. Returns the text unchanged when it holds no retired paragraph verbatim (an operator who
 * rewrote the paragraph keeps their wording).
 */
export function upgradeRetiredCallingCopy(text: string): string {
  return text
    .replace(RETIRED_REVIEW_NOTES_IN_APP_CALLING, REVIEW_NOTES_CALLING)
    .replace(RETIRED_REVIEW_NOTES_RINGING_WITHOUT_RECORDING, REVIEW_NOTES_CALLING)
    .replace(RETIRED_REVIEW_NOTES_CALLING_LONG, REVIEW_NOTES_CALLING)
    .replace(RETIRED_REVIEW_NOTES_AFTER_APPROVAL, `${REVIEW_NOTES_EXTENSIONS}\n${SIGN_IN_FACT}\n`)
    .replace(RETIRED_APP_ACCESS_IN_APP_CALLING, APP_ACCESS_CALLING)
    .replace(RETIRED_APP_ACCESS_RINGING_LIVE_CALL, APP_ACCESS_CALLING)
    .replace(RETIRED_DEMO_SCHOOL, DEMO_SCHOOL)
}

const REVIEW_NOTES = (v: StoreListingDefaultVars) =>
  `DISTRIBUTION\n\nThis app requires public distribution and serves ${v.displayName}’s community, primarily parents and students. Users are families from the general public whose children attend the school. It is not an internal workforce application.\n\n` +
  `Accounts are created when a family enrolls a child at ${v.displayName}. There is no paid content.\n\n` +
  'HOW TO REVIEW\n' +
  '- No credentials needed: tap "Demo login" on the login screen.\n' +
  `- It opens ${DEMO_SCHOOL}. Real school data stays private.\n` +
  '- Check: bottom tabs and side menu, Home welcome card, Feed, student directory (classes, schedules), Groups, test notifications.\n' +
  `${REVIEW_NOTES_SIGN_IN}\n\n` +
  REVIEW_NOTES_CALLING +
  REVIEW_NOTES_EXTENSIONS

const APP_ACCESS_INSTRUCTIONS = (v: StoreListingDefaultVars) =>
  `${v.displayName} is a school community app. This app requires public distribution and is not private to the organization: it serves the school's community — parents and students, who are not employees, partners, or contractors of the school. Any family that enrolls a child receives access; enrollment is open to the general public, with no corporate affiliation required. There is no paid content in the app.\n\n` +
  `No credentials are needed to review: on the login screen, tap the "Demo login" button — it signs you straight in to ${DEMO_SCHOOL}. We cannot expose real school data since the app carries private student information. Things to review: navigation (bottom tabs + drawer/side menu), Home welcome card, Feed demo content, student directory (fake people, classes, schedules), Groups test group with sample posts, and test notifications. ` +
  SIGN_IN_FACT +
  APP_ACCESS_CALLING

const CONTACT_EMAIL = 'apps@schooljourney.com'

/**
 * Play's store listing contact block — Ruben's, per the review contact in
 * `apps/browser-runner/templates/asc-listing.json`.
 *
 * It MUST match `templates/play-store-settings.json`, because both write the same field: the
 * android-setup "Store listing" step pushes this through `edits.details.contactEmail`, and the
 * browser step sets it on the Store settings page. When they disagreed, whichever ran last won and
 * the value flapped between runs.
 *
 * Unlike Apple's review contact, these are shown publicly on the Play store page.
 */
export const PLAY_CONTACT_EMAIL = 'ruben@schooljourney.com'

/**
 * The phone shown beside it on the Play store page.
 *
 * Exported and pushed straight from here rather than read off the listing row, because
 * `MobileAppStoreListing` has no `contact_phone` column and this value is identical for every
 * school — adding a column for one fleet constant would cost a PlanetScale promotion and buy
 * nothing. If a school ever needs its own, that is the moment to add the column.
 */
export const PLAY_CONTACT_PHONE = '+1 818-860-0889'

/**
 * Plain per-store suggestions, with no side channel.
 *
 * The reviewer text used to sit in a `session` group beside these because it had no column and the
 * dialog kept it in `sessionStorage`. It has columns now (SCH-1936), so it is suggested, saved and
 * read exactly like every other field — which is what lets the editor's ordinary field loop render
 * it and the ordinary Save persist it.
 */
export type StoreListingDefaults = Record<StoreListingStore, Partial<StoreListingRecord>>

/** Template defaults, without URLs (the caller adds those once it has checked they resolve). */
export function buildStoreListingDefaults(vars: StoreListingDefaultVars): StoreListingDefaults {
  const title = vars.displayName.slice(0, 30)
  return {
    apple: {
      title,
      description: DESCRIPTION(vars),
      keywords: vars.displayName.slice(0, 100),
      whatsNew: WHATS_NEW,
      contactEmail: CONTACT_EMAIL,
      primaryCategory: 'Reference',
      secondaryCategory: 'Education',
      appleReviewNotes: REVIEW_NOTES(vars)
    },
    play: {
      title,
      shortDescription: shortDescription(vars),
      description: DESCRIPTION(vars),
      whatsNew: WHATS_NEW,
      contactEmail: PLAY_CONTACT_EMAIL,
      primaryCategory: 'Education',
      playAppAccessInstructions: APP_ACCESS_INSTRUCTIONS(vars)
    }
  }
}
